import { Sigma } from 'lucide-react';
import { TooltipButton } from './TooltipButton';
import type { LineItemRow } from '../shared/desktop-api';
import { lineItemArithmetic, lineItemSum, money } from '../shared/line-item-arithmetic';

/**
 * Arithmetic belongs beside the amount, not in a second column of checkmarks
 * competing with human review. Both docked and detached ledgers use this hint.
 * Calculations and correction actions are unchanged; routine successful checks
 * remain available to assistive technology without occupying another column.
 */
export function LineItemCheck({
  id,
  item,
  onAccept,
  onUseSum,
  accepting,
}: {
  id: string;
  item: LineItemRow;
  onAccept: () => void;
  onUseSum: (value: number) => void;
  accepting: boolean;
}) {
  const check = lineItemArithmetic(item);
  // The row has the two numbers that make the third, and the third is missing.
  // Offered, not filled in: a row the reader could not find an amount for is
  // worth a glance, and a number nobody has looked at is how a wrong total
  // becomes a confident one.
  if (check.verdict === 'computable')
    return (
      <div id={id} className="line-check computable">
        <button
          type="button"
          className="line-check-use"
          onClick={() => onUseSum(check.expected)}
          disabled={accepting}
          aria-label={`Set line ${item.rowNumber} amount to ${money(check.expected)}`}
        >
          <Sigma size={13} />
          Use {money(check.expected)}
        </button>
      </div>
    );
  // Accepted by a person, though the multiplication does not work. Said plainly
  // rather than shown as a plain tick: the next operator should be able to tell
  // "this adds up" from "somebody looked at this and it is right anyway".
  if (check.verdict === 'off' && item.arithmeticValidated)
    return (
      <div id={id} className="line-check accepted">
        Accepted as printed
      </div>
    );
  if (check.verdict === 'unchecked')
    return (
      <span id={id} className="visually-hidden">
        Not checked: quantity, unit price and amount are needed.
      </span>
    );

  if (check.verdict === 'balanced')
    return (
      <span id={id} className="visually-hidden">
        {lineItemSum(item.quantity as number, item.unitPrice as number, check.discountPercent)} ={' '}
        {money(check.expected)}. Amount matches.
      </span>
    );
  if (check.verdict === 'credit')
    return (
      <span id={id} className="visually-hidden">
        {lineItemSum(item.quantity as number, item.unitPrice as number, check.discountPercent)} ={' '}
        {money(check.expected)}, taken off as a credit.
      </span>
    );
  return (
    <details id={id} className="line-check bad">
      <summary>
        <Sigma size={13} />
        Check amount
      </summary>
      {/* The sum is the control. An operator who agrees with the arithmetic was
          retyping a number the screen had already worked out and was showing
          them, so the number itself takes the click. */}
      <b>
        {lineItemSum(item.quantity as number, item.unitPrice as number, check.discountPercent)} =
        <TooltipButton
          type="button"
          className="line-check-use"
          onClick={() => onUseSum(check.expected)}
          disabled={accepting}
          tooltip="Use this amount"
        >
          {money(check.expected)}
        </TooltipButton>
      </b>
      <small>
        amount is {money(Math.abs(check.difference))} {check.difference > 0 ? 'more' : 'less'}
      </small>
      <TooltipButton
        type="button"
        className="line-check-accept"
        onClick={onAccept}
        disabled={accepting}
        tooltip="Stop asking about this row"
      >
        Looks right
      </TooltipButton>
    </details>
  );
}
